import React, {useEffect, useRef, useState} from 'react';
import _ from 'lodash';
import {getDatabase, ref, onValue} from "firebase/database";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {EmojiEvents, MoreHoriz} from "@mui/icons-material";
import {Flex} from "reflexbox";
import Typography from "@mui/material/Typography";
import secondsToHMSformat from "../helpers/secondsToHMSformat";
import Button from "@mui/material/Button";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import {createAlert} from "../../redux/uiSlice";
import {messageType} from "../common/RequestAlertBar";

export function ordinal_suffix_of(i) {
  const j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return  "st";
  }
  if (j === 2 && k !== 12) {
    return  "nd";
  }
  if (j === 3 && k !== 13) {
    return  "rd";
  }
  return  "th";
}

const Leaderboard = (props) => {
  const { courseName, setRank } = props;
  const [teams, setTeams] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [timeframe, setTimeframe] = useState('All Time');
  const currentTeamId = useSelector((state) => state.team?.currentTeamId);
  const previousRankRef = useRef(null);
  const dispatch = useDispatch();
  let navigate = useNavigate();

  useEffect(() => {
    if (!courseName) return;
    previousRankRef.current = null; // reset when course changes so we don't false-alert
    const db = getDatabase();
    const leaderboardRef = ref(db, `leaderboard/${courseName}`);
    const unsubscribe = onValue(leaderboardRef, (snapshot) => {
      if (snapshot.exists()) {
        const response = _.orderBy(snapshot.val(), ['totalScore'], ['desc']);
        setTeams(response);
      } else {
        setTeams([]);
      }
      setLoaded(true);
    }, (error) => {
      console.error('Leaderboard load error:', error);
      setTeams([]);
      setLoaded(true);
    });
    return () => unsubscribe();
  }, [courseName]);

  // Compute user's rank from ordered teams (1-based; 0 if not found)
  const userRank = (() => {
    let counter = 1;
    for (let i = 0; i < teams.length; i++) {
      if (currentTeamId === teams[i]?.teamId) return counter;
      counter += 1;
    }
    return 0;
  })();

  // Sync rank to parent when it changes (avoids calling setRank during render)
  useEffect(() => {
    if (setRank) setRank(userRank || null);
  }, [userRank, setRank]);

  // Alert when leaderboard update changes the user's rank (skip initial load)
  useEffect(() => {
    if (!loaded || !currentTeamId) return;
    const currentRank = userRank || null;
    if (previousRankRef.current !== null && previousRankRef.current !== currentRank) {
      dispatch(createAlert({
        message: 'Another team just finished — your rank may have changed.',
        type: messageType.info,
      }));
    }
    previousRankRef.current = currentRank;
  }, [teams, loaded, currentTeamId, userRank, dispatch]);

  const columns = [
    {headerName: 'Rank'},
    {headerName: 'Name'},
    { headerName: 'Score'},
  ];
  const timeframeOptions = ['24h', 'Week', 'All Time'];

  const MS_PER_DAY = 86400000;
  let reducedTeams = [...teams] || [];
  const now = new Date();
  if (!!teams && timeframe !== timeframeOptions[2]) {
    const daysLimit = timeframe === timeframeOptions[0] ? 1 : 7;
    const cutoff = now.getTime() - daysLimit * MS_PER_DAY;
    reducedTeams = teams.filter((obj) => {
      const rawDate = obj?.completedAt || obj?.day;
      const teamDate = rawDate ? new Date(rawDate) : null;
      if (!teamDate || isNaN(teamDate.getTime())) return false;
      return teamDate.getTime() >= cutoff;
    });
  }

  //logic that defines top three teams. Note the edge case of there only being 1-2 teams
  let topThreeTeams;
  if (reducedTeams?.length >= 3) {
    topThreeTeams = [reducedTeams[0], reducedTeams[1], reducedTeams[2]];
  } else {
    topThreeTeams = reducedTeams;
  }
  //iterate over the top 3 teams to render the rows
  let tableContent;
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
  const baseCellStyle = {paddingLeft: '16px', paddingRight: '16px'};
  if (topThreeTeams?.length > 0) {
    tableContent = reducedTeams.map((el, key) => {
      const rawDate = el.completedAt || el.day;
      const parsedDate = rawDate ? new Date(rawDate) : null;
      const isCurrentTeam = el.teamId === currentTeamId;
      if (!props.completeList && key > 2 && !isCurrentTeam) {
        return null;
      }
      return (<React.Fragment key={el.teamId ?? key}>
        {!props.completeList && isCurrentTeam && key > 2 && <TableRow id={'three dots'} onClick={() => navigate(`/leaderboard/${courseName}`)}>
          <TableCell colSpan="3"> <Flex style={{justifyContent: 'center', alignItems: 'center'}}><MoreHoriz sx={{ color: 'text.secondary' }} /></Flex></TableCell>
        </TableRow>}
        <TableRow>
          <TableCell id={'rank'} align={'center'} style={baseCellStyle}> {key !== 0 ? key + 1 :
            <EmojiEvents fontSize="small" style={{color: 'orange'}}/>}
          </TableCell>
          <TableCell id={'team name'} style={{width: '100%', ...baseCellStyle}}>
            <Flex style={{flexDirection: 'column'}}>
              <div>{el.teamName}{isCurrentTeam && ' (Your team)'}</div>
              {parsedDate && !isNaN(parsedDate.getTime()) && (
                <Typography variant="caption" color="text.secondary">
                  {`${month[parsedDate.getMonth()]} ${parsedDate.getDate()}, ${parsedDate.getFullYear()}`}
                </Typography>
              )}
            </Flex>
          </TableCell>
          <TableCell id={'score'} align={'center'} style={baseCellStyle}>
            <Flex style={{flexDirection: 'column', alignItems: 'center'}}>
              <div>{Math.floor(el.totalScore)}</div>
              {typeof el.time === 'number' && (
                <Typography variant={'caption'} style={{color: 'gray'}}>
                  {secondsToHMSformat(el.time)}
                </Typography>
              )}
            </Flex>
          </TableCell>
        </TableRow>
      </React.Fragment>)
    })
  }
  return (<>
    {(!!teams && loaded) ? <>
      <Flex style={{paddingBottom: '8px'}}>
        {timeframeOptions.map((el, key) => (
          <Button key={key} variant={timeframe === el ? 'contained' : 'outlined'} size={'small'}
                  onClick={() => setTimeframe(el)} color={'primary'}
                  style={{width: '100%', paddingTop: 0, paddingBottom: 0, height: 'fit-content'}}>{el}</Button>))}
      </Flex>
      <Table size="small">
        <TableHead sx={{ bgcolor: 'action.hover' }}>
          {columns.map((el, key) => (
            <TableCell key={key} style={{textAlign: el.headerName !== 'Name' && 'center', ...baseCellStyle}} >
              <Typography variant={'caption'} style={{textTransform: 'uppercase'}}>{el.headerName}</Typography>
            </TableCell>))}
        </TableHead>
        <TableBody>
          {tableContent}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>
        Total: {reducedTeams?.length}
      </Typography>
    </> :
      <Flex style={{justifyContent: 'center'}}>
        <CircularProgress size={40} />
      </Flex>}
  </>)
};

export default Leaderboard;